import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faToggleOn, 
  faToggleOff,
  faCheckCircle,
  faCircle,
  faEye,
  faFilter
} from '@fortawesome/free-solid-svg-icons';

const TestOptions = ({ options, onChange, priorityOptions = [], theme }) => {
    
  const styles = {
    container: {
      display: 'grid',
      gap: '1rem',
    },
    card: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '1.5rem',
      border: `1px solid ${theme?.primary?.gray?.[300]}`,
      boxShadow: theme?.shadows?.base,
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    toggleLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    icon: {
      fontSize: '1.25rem',
      color: theme?.primary?.orange?.[500],
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.primary?.gray?.[900],
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme?.primary?.gray?.[600],
      marginTop: '0.25rem',
    },
    toggle: {
      fontSize: '2rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    toggleOn: {
      color: theme?.primary?.green?.[500],
    },
    toggleOff: {
      color: theme?.primary?.gray?.[400],
    },
    priorityTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.primary?.gray?.[900],
      marginBottom: '1.25rem',
    },
    priorityOptions: {
      display: 'grid',
      gap: '0.75rem',
    },
    priorityOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: theme?.primary?.gray?.[100],
      border: `0px solid ${theme?.primary?.gray?.[200]}`,
    },
    priorityOptionHover: {
      background: theme?.primary?.gray?.[100],
      transform: 'translateX(5px)',
      borderColor: theme?.primary?.gray?.[300],
    },
    priorityOptionActive: {
      background: theme?.primary?.orange?.[100],
      borderColor: theme?.primary?.orange?.[300],
    },
    radioIcon: {
      fontSize: '1rem',
    },
    priorityText: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme?.primary?.gray?.[900],
    },
  };

  const handleToggleShowAnswer = () => {
    onChange({
      ...options,
      showAnswer: !options.showAnswer
    });
  };

  const handlePriorityChange = (priority) => {
    onChange({
      ...options,
      priority: priority
    });
  };

  return (
    <div style={styles.container}>
      {/* Show Answer Toggle */}
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div style={styles.toggleContainer}>
          <div>
            <div style={styles.toggleLabel}>
              <FontAwesomeIcon icon={faEye} style={styles.icon} />
              <h3 style={styles.title}>Show Answer As I Go</h3>
            </div>
            <p style={styles.subtitle}>
              View correct answers immediately after each question
            </p>
          </div>
          
          <motion.div
            onClick={handleToggleShowAnswer}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FontAwesomeIcon 
              icon={options.showAnswer ? faToggleOn : faToggleOff} 
              style={{
                ...styles.toggle,
                ...(options.showAnswer ? styles.toggleOn : styles.toggleOff)
              }}
            />
          </motion.div>
        </div>
      </motion.div>
      
      {/* Priority Selection */}
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h3 style={styles.priorityTitle}>
          <FontAwesomeIcon icon={faFilter} style={styles.icon} />
          Question Priority
        </h3>
        
        <div style={styles.priorityOptions}>
          {priorityOptions.map((priority, index) => {
            const isActive = options.priority === priority.key;
            
            return (
              <motion.div
                key={priority.key}
                style={{
                  ...styles.priorityOption,
                  ...(isActive && styles.priorityOptionActive),
                }}
                onClick={() => handlePriorityChange(priority.key)}
                whileHover={styles.priorityOptionHover}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <FontAwesomeIcon 
                  icon={isActive ? faCheckCircle : faCircle} 
                  style={{
                    ...styles.radioIcon,
                    color: isActive ? theme?.primary?.orange?.[500] : theme?.primary?.gray?.[400]
                  }}
                />
                <span style={styles.priorityText}>{priority.title}</span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default TestOptions;